import { Alert, Box, Checkbox, FormControlLabel, TextField, Typography } from '@mui/material';
import { ScheduledMaintenanceWindow } from 'RealmModule/services/scheduledMaintenanceApi';
import { toEpochSeconds } from '../windowTime';

const formatWindow = (window: ScheduledMaintenanceWindow) => {
  if (window.startTime === null || window.endTime === null) return window.value ?? 'unparseable';
  return `${new Date(window.startTime * 1000).toLocaleString()} → ${new Date(
    window.endTime * 1000,
  ).toLocaleString()}`;
};

/**
 * The maintenance window for the migrating team.
 *
 * Galaxy sets this as a GrowthBook rule scoped to (tenant, realm, team), and validates the
 * team against the realm it is CURRENTLY indexed in — which before a migration is the
 * source realm. That is why this reads the source coordinates and not the target.
 */
export const MaintenanceWindowFields = ({
  enabled,
  onEnabledChange,
  end,
  onEndChange,
  existingWindow,
  flagDisabled,
  unwritableRule,
  disabled,
}: {
  enabled: boolean;
  onEnabledChange: (enabled: boolean) => void;
  end: string;
  onEndChange: (value: string) => void;
  existingWindow?: ScheduledMaintenanceWindow;
  /** The flag is off for this environment — a set would 409. */
  flagDisabled?: boolean;
  /**
   * A non-`force` rule on the flag, which makes the whole flag unwritable — including for
   * teams it has nothing to do with. Passed whole so the copy can identify it.
   */
  unwritableRule?: ScheduledMaintenanceWindow;
  disabled?: boolean;
}) => {
  const endSeconds = toEpochSeconds(end);
  const endMalformed = enabled && !!end && endSeconds === null;

  // "Is the end in the future" is deliberately NOT checked here. It depends on the current
  // time, so a value computed during render is stale the moment it is painted — the field
  // would look valid until something unrelated re-rendered it. Galaxy requires it, and the
  // form checks it at submit, which is the only moment the answer is true.

  return (
    <Box gap={12} display="flex" flexDirection="column">
      <Typography variant="subtitle2">Maintenance window</Typography>

      <FormControlLabel
        control={
          <Checkbox
            checked={enabled}
            onChange={(e) => onEnabledChange(e.target.checked)}
            disabled={disabled}
          />
        }
        label="Put the team into maintenance for this window"
      />

      <Typography variant="caption" color="text.secondary">
        Sets the <code>scheduledMaintenanceFlag</code> rule for this team before the migration
        starts, so the product shows a maintenance state while its data is moving. Scoped to the
        source tenant and realm, because that is where the team is indexed until the migration
        completes.
      </Typography>

      {enabled && (
        <Box display="flex" gap={2} flexWrap="wrap">
          {/*
            No start field. The migration begins the moment Start is pressed, so the only
            window that makes sense opens then: a later one would leave the team serving
            traffic while its data moved, and an earlier one is just a stale default
            quietly eating the operator's headroom on a form that has been sitting open.
            The start is stamped at submit instead.
          */}
          <TextField
            label="Window ends at"
            type="datetime-local"
            value={end}
            onChange={(e) => onEndChange(e.target.value)}
            slotProps={{ inputLabel: { shrink: true } }}
            disabled={disabled}
            error={endMalformed}
            helperText={
              endMalformed
                ? 'Use the date picker — a date on its own is read as UTC midnight.'
                : 'The window opens when you press Start. A migration takes 15-90 minutes; leave headroom.'
            }
            sx={{ flex: 1, minWidth: 240 }}
          />
        </Box>
      )}

      {/* Suppressed while the flag is unwritable: "scheduling replaces it" and "scheduling
          will be refused" are contradictory, and the refusal is the one that decides. */}
      {!!existingWindow && !unwritableRule && (
        <Alert severity="warning">
          This team already has a galaxy maintenance window: {formatWindow(existingWindow)}
          {/* isActive ignores GrowthBook's own schedule gate, so it is only an answer for a
              rule that carries none — for the rest, claiming "active now" would be a guess
              stated as fact. */}
          {!existingWindow.hasScheduleRules && existingWindow.isActive ? ' (active now)' : ''}.
          Scheduling replaces it.
          {existingWindow.hasScheduleRules &&
            ' It also carries GrowthBook schedule rules, which galaxy does not write and cannot evaluate, so whether it is serving right now can only be seen in GrowthBook.'}
        </Alert>
      )}

      {enabled && flagDisabled && (
        <Alert severity="error">
          The maintenance flag is disabled for this environment, so scheduling will be refused.
          Enable it in GrowthBook, or untick the box to migrate without a window.
        </Alert>
      )}

      {enabled && !!unwritableRule && (
        <Alert severity="error">
          A <code>{unwritableRule.type}</code> rule on this flag blocks scheduling: galaxy only
          writes <code>force</code> rules and refuses to touch a flag carrying any other kind.
          {/* Named because the rule is often nothing to do with this team — the refusal is
              flag-wide — so the operator needs enough to find whoever owns it. */}
          {(unwritableRule.ruleId || unwritableRule.description) && (
            <Box mt={1}>
              The rule is <code>{unwritableRule.ruleId ?? unwritableRule.description}</code>
              {unwritableRule.ruleId && unwritableRule.description
                ? ` (${unwritableRule.description})`
                : ''}
              . It may belong to another team — it blocks this flag for everyone either way, and has
              to be removed in GrowthBook.
            </Box>
          )}
        </Alert>
      )}

      {enabled && (
        <Typography variant="caption" color="text.secondary">
          The window is not cleared automatically. It stops applying at its end time, but the rule
          itself remains on the flag — and once the team re-indexes into the target realm galaxy can
          no longer address it, so remove it in GrowthBook if it needs to go.
        </Typography>
      )}
    </Box>
  );
};

export default MaintenanceWindowFields;
